import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { usePackageKPIs } from "@/hooks/usePackageKPIs";
import { Activity, BarChart3, Users, AlertCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";

export const PackageKPIWidget = () => {
  const { data: kpis, isLoading } = usePackageKPIs();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-amber-600" />
          مؤشرات أداء الباقات (KPIs)
        </CardTitle>
        <CardDescription>مؤشرات الأداء الرئيسية للباقات السياحية</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Conversion Rate */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                <span className="font-semibold">معدل التحويل</span>
              </div>
              <span className="text-2xl font-bold text-blue-600">{kpis?.conversionRate || 0}%</span>
            </div>
            <Progress value={kpis?.conversionRate || 0} className="h-2" />
            <p className="text-xs text-muted-foreground mt-2">
              {kpis?.totalBookings || 0} حجز من {kpis?.totalViews || 0} مشاهدة
            </p>
          </div>

          {/* Customer Satisfaction */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-green-600" />
                <span className="font-semibold">رضا العملاء</span>
              </div>
              <span className="text-2xl font-bold text-green-600">
                {kpis?.customerSatisfaction.toFixed(1) || 0}/5
              </span>
            </div>
            <Progress value={(kpis?.customerSatisfaction || 0) * 20} className="h-2" />
            <p className="text-xs text-muted-foreground mt-2">
              متوسط التقييمات من العملاء
            </p>
          </div>

          {/* Average Booking Value */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                <span className="font-semibold">متوسط قيمة الحجز</span>
              </div>
              <span className="text-2xl font-bold text-purple-600">
                {kpis?.averageBookingValue.toLocaleString() || 0} ريال
              </span>
            </div>
          </div>

          {/* Cancellation Rate */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="font-semibold">معدل الإلغاء</span>
              </div>
              <span className="text-2xl font-bold text-red-600">{kpis?.cancellationRate || 0}%</span>
            </div>
            <Progress value={kpis?.cancellationRate || 0} className="h-2 bg-red-100" />
          </div>

          {/* Repeat Customer Rate */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                <span className="font-semibold">معدل العملاء المتكررين</span>
              </div>
              <span className="text-2xl font-bold text-indigo-600">{kpis?.repeatBookingRate || 0}%</span>
            </div>
            <Progress value={kpis?.repeatBookingRate || 0} className="h-2" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
